import { useState, useEffect } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';

interface Week {
  id: string;
  week_start_date: string;
}

interface Detail {
  id: string;
  employee_id: string;
  day_of_week: number;
  branch_id: string | null;
  employee_code: string;
  first_name: string;
  last_name: string;
  branch_name: string | null;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function BranchSchedule() {
  const [weeks, setWeeks] = useState<Week[]>([]);
  const [selectedWeek, setSelectedWeek] = useState<string>('');
  const [newWeekStart, setNewWeekStart] = useState('');
  const [employees, setEmployees] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [grid, setGrid] = useState<Record<string, Record<number, string>>>({});

  useEffect(() => {
    fetchWeeks();
    api.get('/employees').then((r) => setEmployees(r.data.employees || []));
    api.get('/tms/branches').then((r) => setBranches(r.data.branches || []));
  }, []);

  useEffect(() => {
    if (selectedWeek) fetchDetails(selectedWeek);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeek]);

  const fetchWeeks = async () => {
    const res = await api.get('/tms/branch-schedules/weeks');
    setWeeks(res.data.weeks || []);
    if (res.data.weeks?.length) setSelectedWeek(res.data.weeks[0].id);
  };

  const fetchDetails = async (weekId: string) => {
    const res = await api.get(`/tms/branch-schedules/weeks/${weekId}`);
    const rows: Detail[] = res.data.details || [];
    const g: Record<string, Record<number, string>> = {};
    rows.forEach((r) => {
      g[r.employee_id] = g[r.employee_id] || {};
      g[r.employee_id][r.day_of_week] = r.branch_id || '';
    });
    setGrid(g);
  };

  const createWeek = async () => {
    if (!newWeekStart) return alert('Pick a Monday start date');
    const res = await api.post('/tms/branch-schedules/weeks', { weekStartDate: newWeekStart });
    setNewWeekStart('');
    await fetchWeeks();
    setSelectedWeek(res.data.week.id);
  };

  const deleteWeek = async (id: string) => {
    if (!window.confirm('Delete this week?')) return;
    await api.delete(`/tms/branch-schedules/weeks/${id}`);
    fetchWeeks();
  };

  const setCell = (employeeId: string, day: number, branchId: string) => {
    setGrid((prev) => ({ ...prev, [employeeId]: { ...prev[employeeId], [day]: branchId } }));
  };

  const saveGrid = async () => {
    const entries: any[] = [];
    Object.entries(grid).forEach(([employeeId, days]) => {
      Object.entries(days).forEach(([day, branchId]) => {
        if (branchId) entries.push({ employeeId, dayOfWeek: Number(day), branchId });
      });
    });
    await api.put(`/tms/branch-schedules/weeks/${selectedWeek}`, { entries });
    alert('Branch schedule saved');
    fetchDetails(selectedWeek);
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">Branch Schedule — Weekly Planner</h1>

      <div className="bg-white rounded-lg shadow p-4 mb-4 flex gap-3 items-end flex-wrap">
        <Select
          label="Week"
          value={selectedWeek}
          onChange={(e) => setSelectedWeek(e.target.value)}
          options={weeks.map((w) => ({ value: w.id, label: `Week of ${w.week_start_date?.slice(0, 10)}` }))}
        />
        <Input label="New week start (Monday)" type="date" value={newWeekStart} onChange={(e) => setNewWeekStart(e.target.value)} />
        <Button onClick={createWeek}>Create Week</Button>
        {selectedWeek && (
          <Button variant="danger" onClick={() => deleteWeek(selectedWeek)}>
            Delete Week
          </Button>
        )}
      </div>

      {!weeks.length && (
        <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500 text-sm">
          No weeks created yet — pick a Monday start date above and click Create Week.
        </div>
      )}

      {selectedWeek && (
        <div className="bg-white rounded-lg shadow p-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left p-2">Employee</th>
                {DAYS.map((d) => (
                  <th key={d} className="p-2 text-center">{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {employees.map((emp) => (
                <tr key={emp.id} className="border-b">
                  <td className="p-2 whitespace-nowrap">{emp.full_name} ({emp.code})</td>
                  {DAYS.map((_, day) => (
                    <td key={day} className="p-1">
                      <select
                        className="border rounded px-1 py-1 text-xs w-full"
                        value={grid[emp.id]?.[day] || ''}
                        onChange={(e) => setCell(emp.id, day, e.target.value)}
                      >
                        <option value="">—</option>
                        {branches.map((b) => (
                          <option key={b.id} value={b.id}>{b.code}</option>
                        ))}
                      </select>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {!employees.length && (
            <p className="text-center text-gray-500 text-sm py-6">No active employees found.</p>
          )}
          <Button className="mt-4" onClick={saveGrid}>Save Grid</Button>
        </div>
      )}
    </div>
  );
}
